import React from 'react';

import Layout from './Layout';
import Header from './Header';
import Footer from './Footer';
import CompetitionList from './CompetitionList';

const COMPETITIONS_HELP_URL =
  'https://help.erg.zone/article/118-organizing-a-competition-on-ergzone';
const COMPETITIONS_COLLECTION_URL =
  'https://help.erg.zone/collection/102-competitions';

const sectionDarkStyle = {
  background: '#333333',
  backgroundImage: 'none',
};

const openStartCompetitionBeacon = () => {
  if (typeof window === 'undefined') return;

  if (window.plausible) {
    window.plausible('StartCompetition Click');
  }
  const content = `I'm interested in running a competition on ErgZone.\n\n Event Name:\n Event Type (erg / hybrid / in-person):\n Date(s):\n Brief description:`;
  if (window.Beacon) {
    window.Beacon('prefill', {
      subject: 'New Competition',
      text: content,
    });
    window.Beacon('navigate', '/ask/message');
    window.Beacon('open');
  }
};

const StartCompetitionButton = ({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) => (
  <a
    href="#"
    className={className}
    style={style}
    onClick={(e) => {
      e.preventDefault();
      openStartCompetitionBeacon();
    }}
  >
    Start a Competition
  </a>
);

const CompetitionsPage = ({ pageContext }) => {
  const competitions = pageContext?.competitions || [];

  return (
    <Layout titlePrefixes={['Competitions']}>
      <style>{`
        .section-dark .section-heading h2,
        .section-dark .section-heading p,
        .section-dark h3,
        .section-dark h4,
        .section-dark p,
        .section-dark .text-muted,
        .section-dark .lead { color: rgba(255, 255, 255, 0.95); }
        .section-dark .feature-item h3,
        .section-dark .feature-item p { color: rgba(255, 255, 255, 0.9); }
        .section-dark .feature-item i { color: #00c9be !important; }
        .section-dark hr { border-color: rgba(255, 255, 255, 0.3); }

        .feature-item i {
          background: -webkit-linear-gradient(to left, #9FF4EB 0%, #00c9be 40%, #00504c 75%, #001210 100%) !important;
          background: linear-gradient(to left, #9FF4EB 0%, #00c9be 40%, #00504c 75%, #001210 100%) !important;
          -webkit-background-clip: text !important;
          background-clip: text !important;
          -webkit-text-fill-color: transparent !important;
        }
      `}</style>
      <Header />

      <header className="masthead text-center" id="top">
        <div className="container">
          <div className="row h-100">
            <div className="col-lg-7 my-auto">
              <div className="header-content mx-auto">
                <h1 className="mb-4">Competitions on ErgZone</h1>
                <p className="lead mb-4">
                  ErgZone is the ultimate platform for hosting fitness
                  competitions. Whether you are running a high-stakes live event
                  at your gym or a global virtual challenge, our platform
                  eliminates the administrative headache of paper scoring and
                  gives your athletes a professional, real-time leaderboard
                  experience.
                </p>
                <div className="d-flex flex-column flex-sm-row">
                  <StartCompetitionButton
                    className="btn btn-outline btn-xl mr-sm-3 mb-2 mb-sm-0"
                    style={{ whiteSpace: 'nowrap' }}
                  />
                  <a
                    href="https://bookademo.erg.zone/"
                    className="btn btn-outline btn-xl plausible-event-name=BookCompetitionDemo+Click"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book a demo
                  </a>
                </div>
              </div>
            </div>
            <div className="col-lg-5 my-auto">
              <div className="device-container">
                <div
                  style={{
                    position: 'relative',
                    aspectRatio: '9/16',
                    maxWidth: '325px',
                    margin: '0 auto',
                    overflow: 'hidden',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(0,0,0,0.35)',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <iframe
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: '12px',
                      }}
                      src="https://www.youtube.com/embed/FZDv655Xfas?rel=0"
                      referrerpolicy="strict-origin-when-cross-origin"
                      title="ErgZone Competitions Overview"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      frameBorder="0"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="features" id="types">
        <div className="container">
          <div className="section-heading text-center">
            <h2>Types of Competitions</h2>
            <p className="text-muted">
              Built for every way your athletes compete.
            </p>
            <hr />
          </div>
          <div className="row">
            <div className="col-lg-6">
              <div className="feature-item">
                <i className="fas fa-bolt text-primary"></i>
                <h3>
                  Real-Time Erg
                  <br />
                  Racing
                </h3>
                <p className="text-muted">
                  Stream performance metrics in real time to a big screen with
                  the Live Dashboard. Results sync automatically from the
                  monitor to the leaderboard at the end of the workout, with
                  full control to keep standings live or hidden for a final
                  reveal.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="feature-item">
                <i className="fas fa-globe-americas text-primary"></i>
                <h3>Global Virtual Challenges</h3>
                <p className="text-muted">
                  Connect athletes from anywhere in the world. Participants
                  compete on their own time from their local gym or home, with
                  results flowing into a unified global leaderboard for a true
                  community experience.
                </p>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6">
              <div className="feature-item">
                <i className="fas fa-clipboard-list text-primary"></i>
                <h3>Functional &amp; Mixed-Modality</h3>
                <p className="text-muted">
                  Built for every fitness format, with or without connected
                  ergs. Empower your team with a digital scorekeeper to enter
                  and verify results instantly on any device. Keep your event
                  organized and professional with paperless scoring that updates
                  your leaderboard in real time.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="feature-item">
                <i className="fas fa-trophy text-primary"></i>
                <h3>Single or Multi-Workout</h3>
                <p className="text-muted">
                  Flexibility for any event timeline. Host a focused, one-off
                  championship or a season-long series with multiple stages and
                  events. The platform automatically tracks progress across
                  every event to determine the overall winners.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="features section-dark"
        id="how-it-works"
        style={sectionDarkStyle}
      >
        <div className="container">
          <div className="section-heading text-center">
            <h2>How It Works</h2>
            <p className="text-muted">From registration to leaderboard</p>
            <hr />
          </div>
          <div className="row">
            <div className="col-lg-4">
              <div className="feature-item">
                <i
                  className="fas fa-user-plus"
                  style={{ color: '#00c9be' }}
                ></i>
                <h3>Registration</h3>
                <p className="text-muted">
                  Tailor your entry process with free registration, direct
                  payments, or manual approval. Organize your field easily by
                  setting custom divisions, teams, and athlete data fields to
                  fit your event.
                </p>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="feature-item">
                <i className="fas fa-bolt" style={{ color: '#00c9be' }}></i>
                <h3>Competition</h3>
                <p className="text-muted">
                  Athletes perform workouts through the ErgZone app, which
                  manages the session for them. For hybrid or in-person events,
                  judges or athletes submit scores directly for instant,
                  verified results.
                </p>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="feature-item">
                <i
                  className="fas fa-chart-bar"
                  style={{ color: '#00c9be' }}
                ></i>
                <h3>Leaderboard</h3>
                <p className="text-muted">
                  Standings update in real-time as erg results sync instantly to
                  the public board. Organizers maintain full control through a
                  central dashboard to manage, edit, or finalize scores as the
                  race unfolds.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="features" id="browse-competitions">
        <div className="container">
          <div className="section-heading text-center">
            <h2>Explore Competitions</h2>

            <p className="text-muted">
              Find events open for registration, follow competitions in
              progress, and browse results from recent challenges. From local
              throwdowns to global championships, use the filters below to
              search by sport, organizer, or year.
            </p>
            <hr />
          </div>
          <CompetitionList competitions={competitions} />
        </div>
      </section>

      <section className="features" id="faq">
        <div className="container">
          <div className="section-heading text-center">
            <h2>Frequently Asked Questions</h2>
            <hr />
          </div>

          <div className="row">
            <div className="col-lg-10 mx-auto">
              <h3 className="mb-4 mt-5">For Organizers</h3>

              <div className="mb-4">
                <h4 className="mb-2">What types of competitions can I run?</h4>
                <p className="text-muted">
                  You can host erg-only competitions with automated data
                  collection, or hybrid and in-person events where you manage
                  scoring. Options include single-workout sprints, global
                  virtual challenges, and multi-workout championships with
                  custom divisions and team categories.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">How do I set up a competition?</h4>
                <p className="text-muted">
                  To ensure your event is a success, our team assists with the
                  initial setup. Contact us with your event details—including
                  the title, start and end dates, registration window, and
                  divisions—and we will help configure your competition. Once
                  live, you&apos;ll have full access to manage your athletes and
                  leaderboard through our step-by-step guides.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">
                  Can I show the results on a screen at my event?
                </h4>
                <p className="text-muted">
                  Yes. For erg competitions using the ErgZone app, you have
                  access to a Live Dashboard designed for big-screen displays
                  that streams real-time data. For non-erg or manual events, the
                  Live Leaderboard serves as your digital scoreboard, updating
                  the standings the moment a performance is reported.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">
                  Do I need special hardware to run a virtual challenge?
                </h4>
                <p className="text-muted">
                  No. Athletes only need the ErgZone app and a compatible erg
                  monitor. The app handles all data transmission globally,
                  allowing you to host thousands of participants without needing
                  any extra equipment on your end.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">Are there fees for competitions?</h4>
                <p className="text-muted">
                  Competition fees depend on your plan and event size. If you
                  choose to process athlete registrations through the ErgZone
                  Marketplace, standard marketplace transaction fees apply. For
                  all other event types, please check our help article on
                  competition costs for current pricing and included features.
                </p>
              </div>

              <h3 className="mb-4 mt-5">For Athletes</h3>

              <div className="mb-4">
                <h4 className="mb-2">How do I register for a competition?</h4>
                <p className="text-muted">
                  Find your event by heading to the Discover tab in the ErgZone
                  app or by visiting{' '}
                  <a
                    href="https://erg.zone/discover"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    erg.zone/discover
                  </a>
                  . From there, you can select your competition, choose your
                  division, and complete the registration. If the event requires
                  a fee, you will be prompted to complete the checkout process
                  directly within that flow.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">
                  How do my results get on the leaderboard?
                </h4>
                <p className="text-muted">
                  In erg competitions, your results sync automatically when you
                  complete the workout in the app. In hybrid or in-person
                  events, the organizer may enter or import results; check the
                  specific event details for scoring instructions.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">
                  What if I make a mistake or have a technical issue?
                </h4>
                <p className="text-muted">
                  Organizers have full control to edit, verify, or override
                  scores. If an issue occurs, contact the event director; they
                  will evaluate the performance and can manually verify or
                  update your result on the leaderboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="bg-primary text-center"
        id="cta"
        style={{ paddingTop: '60px', paddingBottom: '80px' }}
      >
        <div className="container">
          <div className="row">
            <div className="col-md-8 mx-auto">
              <h2 className="section-heading">Ready to Run a Competition?</h2>
              <p
                className="text-muted"
                style={{ marginTop: '12px', marginBottom: '32px' }}
              >
                Tell us about your event and we&apos;ll help you get set up.
              </p>
              <div style={{ marginBottom: '40px' }}>
                <StartCompetitionButton className="btn btn-light btn-xl" />
              </div>

              <p style={{ marginBottom: '16px' }}>
                Prefer to read up first? Check out our overview or browse the
                full help library for setup guides and FAQs.
              </p>
              <div style={{ marginBottom: '28px' }}>
                <a
                  href={COMPETITIONS_HELP_URL}
                  className="btn btn-outline btn-xl mr-3 mb-2 mb-md-0"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Competitions overview
                </a>
                <a
                  href={COMPETITIONS_COLLECTION_URL}
                  className="btn btn-outline btn-xl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  All help articles
                </a>
              </div>

              <p style={{ marginTop: '8px', paddingBottom: '24px' }}>
                Have questions?{' '}
                <a
                  href="#"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (typeof window !== 'undefined' && window.Beacon) {
                      window.Beacon('prefill', {
                        subject: 'Competition Help',
                        text: '',
                      });
                      window.Beacon('navigate', '/ask/message');
                      window.Beacon('open');
                    }
                  }}
                >
                  Contact us
                </a>{' '}
                and we&apos;ll help you plan your event.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </Layout>
  );
};

export default CompetitionsPage;
